import React, { useMemo, useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import {
  PayrollIcon,
  PlusIcon,
  SearchIcon,
  CloseIcon,
  ChevronDownIcon,
  PrintIcon,
} from "../../Icons";
import { useInvoice } from "./useInvoice";
import CreateInvoiceModal from "./components/CreateInvoiceModal";
import InvoiceHistory from "./components/InvoiceHistory";
import RateModal from "./components/RateModal";
import DeleteConfirmModal from "../Documents/components/DeleteConfirmModal";
import { displayDateUS } from "../../utils/dateUtils";
import useCurrentUser from "../../hooks/useCurrentUser";
import { resolveUserRoleName, normalizeRoleCode } from "../../constants";
import {
  PAGE_SHELL,
  PAGE_HEADER_ROW,
  PAGE_SUBTITLE,
  PAGE_ACTIONS,
} from "../../ui/pageChrome";
import {
  INVOICE_THEME,
  INVOICE_BTN,
  INVOICE_FIELD,
} from "./invoiceTheme";
import { INVOICE_MARKS, isInvoiceEditLocked, selectionMarkMode } from "./invoiceMarks";
import { openInvoiceModulePrint } from "./invoicePrint";

const canManageInvoices = (user) => {
  const code = normalizeRoleCode(resolveUserRoleName(user));
  return code === "ADMIN" || code === "OA";
};

const Invoice = () => {
  const { officeLogo, companyName, user: sessionUser } = useCurrentUser();
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const canManage = canManageInvoices(currentUser || sessionUser);
  const [roleFilter, setRoleFilter] = useState("");

  const {
    methods,
    invoices,
    totalInvoices,
    currentPage,
    setCurrentPage,
    pageSize,
    totalPages,
    currentInvoiceItems,
    editingInvoiceId,
    isLoading,
    showRateModal,
    setShowRateModal,
    showCreateModal,
    openCreateModal,
    openEditModal,
    handleCloseCreateModal,
    visitTypeRates,
    visitTypes,
    rateVisitTypes,
    handleAddItem,
    handleRemoveItem,
    handleSubmitInvoice,
    handleDeleteInvoice,
    handleApplyMarks,
    handleSaveRates,
    totalCurrentAmount,
    searchTerm,
    setSearchTerm,
  } = useInvoice({ enableRoleFilter: canManage, roleFilter });

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [actionOpen, setActionOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const actionBtnRef = useRef(null);
  const actionMenuRef = useRef(null);
  const [actionMenuCoords, setActionMenuCoords] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const onDocClick = (e) => {
      if (actionBtnRef.current?.contains(e.target)) return;
      if (actionMenuRef.current?.contains(e.target)) return;
      setActionOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useLayoutEffect(() => {
    if (!actionOpen) return undefined;

    const place = () => {
      const el = actionBtnRef.current;
      const menu = actionMenuRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const menuW = 224;
      const menuH = menu?.offsetHeight || 280;
      const gap = 4;
      const placeAbove =
        rect.bottom + gap + menuH > window.innerHeight - 8 &&
        rect.top > menuH + gap;
      let left = rect.right - menuW;
      left = Math.max(8, Math.min(left, window.innerWidth - menuW - 8));
      const top = placeAbove ? rect.top - menuH - gap : rect.bottom + gap;
      setActionMenuCoords({ top, left });
    };

    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [actionOpen, canManage]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [currentPage, searchTerm, roleFilter]);

  const formatDate = (dateString) => displayDateUS(dateString);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);

  const roleOptions = useMemo(() => {
    const roles = new Set();
    (invoices || []).forEach((inv) => {
      if (inv.role) roles.add(String(inv.role));
    });
    return [
      { value: "", label: "All Roles" },
      ...Array.from(roles)
        .sort((a, b) => a.localeCompare(b))
        .map((role) => ({ value: role, label: role })),
    ];
  }, [invoices]);

  const hasActiveFilters = Boolean(
    searchTerm.trim() || (canManage && roleFilter),
  );

  const clearFilters = () => {
    setSearchTerm("");
    if (canManage) setRoleFilter("");
  };

  const displayedInvoices = invoices || [];

  /** Invoice-module print only (does not touch SaaS bill print). */
  const openPrintPreview = (invoiceList) => {
    const list = Array.isArray(invoiceList) ? invoiceList : [invoiceList];
    const ok = openInvoiceModulePrint(list.filter(Boolean), {
      companyName,
      officeLogo,
      formatDate,
    });
    if (!ok) {
      toast.error("Please allow pop-ups to print invoices.");
    }
  };

  const handleRequestDelete = (invoice) => {
    setDeleteTarget(invoice);
  };

  const handleToggleRow = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleToggleAll = (pageIds, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      pageIds.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const hasSelection = selectedIds.size > 0;

  const applyMark = async (markType, action = "apply") => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setApplying(true);
    setActionOpen(false);
    try {
      const ok = await handleApplyMarks(ids, markType, action);
      if (ok) setSelectedIds(new Set());
    } finally {
      setApplying(false);
    }
  };

  const bulkPrint = () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) {
      toast.error("Select at least one invoice to print");
      return;
    }
    setActionOpen(false);
    const selected = (invoices || []).filter((inv) => ids.includes(inv.id));
    if (!selected.length) {
      toast.error("Selected invoices not found on this page");
      return;
    }
    // One tab: all selected invoices; each invoice starts on a new printed page.
    openPrintPreview(selected);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleting(true);
    try {
      const ok = await handleDeleteInvoice(deleteTarget.id);
      if (ok) {
        setDeleteTarget(null);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(deleteTarget.id);
          return next;
        });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const employeeLabel = deleteTarget
    ? [deleteTarget.employer?.firstName, deleteTarget.employer?.lastName]
        .filter(Boolean)
        .join(" ") || "this employee"
    : "";

  return (
    <div className={PAGE_SHELL}>
      <div className={PAGE_HEADER_ROW}>
        <div>
          <h1 className={`text-2xl font-bold ${INVOICE_THEME.section.title}`}>
            Invoice Management
          </h1>
          <p className={PAGE_SUBTITLE}>Create and manage your invoices</p>
        </div>
        <div className={PAGE_ACTIONS}>
          <button
            type="button"
            onClick={() => setShowRateModal(true)}
            className={INVOICE_BTN.outline}
          >
            <PayrollIcon size={16} />
            Manage Rates
          </button>
          <button
            type="button"
            onClick={openCreateModal}
            className={INVOICE_BTN.primary}
          >
            <PlusIcon size={16} />
            Create New Invoice
          </button>
        </div>
      </div>

      <CreateInvoiceModal
        show={showCreateModal}
        onClose={handleCloseCreateModal}
        methods={methods}
        onSubmit={handleSubmitInvoice}
        isLoading={isLoading}
        currentInvoiceItems={currentInvoiceItems}
        handleAddItem={handleAddItem}
        handleRemoveItem={handleRemoveItem}
        visitTypes={visitTypes}
        formatDate={formatDate}
        formatCurrency={formatCurrency}
        totalCurrentAmount={totalCurrentAmount}
        isEdit={Boolean(editingInvoiceId)}
      />

      <RateModal
        show={showRateModal}
        onClose={() => setShowRateModal(false)}
        rates={visitTypeRates}
        visitTypes={rateVisitTypes}
        onSave={handleSaveRates}
      />

      <DeleteConfirmModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => !isDeleting && setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Invoice"
        message={`Do you want to delete invoice #${
          deleteTarget?.invoiceNumber || ""
        } for ${employeeLabel}? This action cannot be undone.`}
        isLoading={isDeleting}
      />

      <div
        className={`bg-white rounded-xl border shadow-sm overflow-hidden ${INVOICE_THEME.section.wrap}`}
      >
        <div
          className={`flex items-center gap-2 h-11 px-4 border-b box-border ${INVOICE_THEME.section.head}`}
        >
          <span
            className={`w-0.5 h-4 rounded-full shrink-0 ${INVOICE_THEME.section.bar}`}
            aria-hidden
          />
          <h2
            className={`min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide truncate ${INVOICE_THEME.section.title}`}
          >
            Invoices
          </h2>
          <span
            className={`inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded text-[10px] font-semibold tabular-nums shrink-0 ${INVOICE_THEME.section.badge}`}
          >
            {totalInvoices}
          </span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 px-3 py-2.5 border-b border-indigo-100">
          <div
            className={`relative flex-1 min-w-0 ${canManage ? "" : "sm:max-w-none"}`}
          >
            <SearchIcon
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search by invoice #, employee, notes..."
              className={INVOICE_FIELD.search}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm ? (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <CloseIcon size={14} />
              </button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {canManage ? (
              <div className="relative">
                <label className="sr-only" htmlFor="invoice-role-filter">
                  Filter by role
                </label>
                <select
                  id="invoice-role-filter"
                  className={`${INVOICE_FIELD.select} ${
                    roleFilter ? INVOICE_FIELD.selectActive : ""
                  }`}
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  {roleOptions.map((option) => (
                    <option
                      key={option.value || "all-roles"}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon
                  size={14}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
              </div>
            ) : null}

            <div className="relative" ref={actionBtnRef}>
              <button
                type="button"
                disabled={!hasSelection || applying}
                onClick={() => setActionOpen((v) => !v)}
                className={`inline-flex items-center gap-1 h-8 px-3 text-xs font-medium border rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  hasSelection
                    ? INVOICE_THEME.button.outline
                    : "text-slate-500 bg-white border-slate-200"
                }`}
                title={
                  hasSelection
                    ? canManage
                      ? "Apply action to selected invoices"
                      : "Print selected invoices"
                    : "Select invoices to enable Action"
                }
                aria-haspopup="menu"
                aria-expanded={actionOpen}
              >
                Action
                <ChevronDownIcon size={12} />
              </button>
              {actionOpen
                ? createPortal(
                <div
                  ref={actionMenuRef}
                  role="menu"
                  style={{
                    position: "fixed",
                    top: actionMenuCoords.top,
                    left: actionMenuCoords.left,
                    width: 224,
                    zIndex: 80,
                  }}
                  className="rounded-lg border border-slate-200 bg-white shadow-lg py-1"
                >
                  <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    Select an Action
                  </p>
                  <button
                    type="button"
                    onClick={bulkPrint}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 ${INVOICE_THEME.menuHover}`}
                  >
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-slate-700 text-white shrink-0">
                      <PrintIcon size={12} strokeWidth={2.25} />
                    </span>
                    Print
                  </button>
                  {canManage ? (
                    (() => {
                      const markModes = INVOICE_MARKS.map((mark) => ({
                        mark,
                        mode: selectionMarkMode(
                          displayedInvoices,
                          selectedIds,
                          mark.type,
                        ),
                      }));
                      const applyMarks = markModes.filter(
                        ({ mode }) => mode === "apply" || mode === "both",
                      );
                      const removeMarks = markModes.filter(
                        ({ mode }) => mode === "remove" || mode === "both",
                      );
                      const renderMarkButton = (mark, action) => {
                        const Icon = mark.Icon;
                        return (
                          <button
                            key={`${mark.type}-${action}`}
                            type="button"
                            onClick={() => applyMark(mark.type, action)}
                            className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 ${INVOICE_THEME.menuHover}`}
                          >
                            <span
                              className="inline-flex h-5 w-5 items-center justify-center rounded-md text-white shrink-0"
                              style={{ backgroundColor: mark.color }}
                            >
                              {Icon ? (
                                <Icon size={12} strokeWidth={2.25} />
                              ) : (
                                mark.code
                              )}
                            </span>
                            {mark.label}
                          </button>
                        );
                      };
                      return (
                        <>
                          {applyMarks.length > 0 ? (
                            <>
                              <div className="my-1 border-t border-slate-100" />
                              <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                Mark
                              </p>
                              {applyMarks.map(({ mark }) =>
                                renderMarkButton(mark, "apply"),
                              )}
                            </>
                          ) : null}
                          {removeMarks.length > 0 ? (
                            <>
                              <div className="my-1 border-t border-slate-100" />
                              <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                Remove
                              </p>
                              {removeMarks.map(({ mark }) =>
                                renderMarkButton(mark, "remove"),
                              )}
                            </>
                          ) : null}
                        </>
                      );
                    })()
                  ) : null}
                </div>,
                document.body,
              )
                : null}
            </div>

            {hasActiveFilters ? (
              <button
                type="button"
                onClick={clearFilters}
                className={INVOICE_BTN.clear}
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>

        {hasSelection ? (
          <div className="px-3 py-1.5 border-b border-indigo-50 bg-indigo-50/40 text-[11px] text-slate-600">
            {selectedIds.size} invoice{selectedIds.size === 1 ? "" : "s"}{" "}
            selected — use Action to{" "}
            {canManage ? "Print, Mark, or Remove flags" : "Print"}
          </div>
        ) : null}

        <InvoiceHistory
          invoices={displayedInvoices}
          canDelete={canManage}
          canEditWhenFlagged={canManage}
          canManageFlags={canManage}
          canSelect
          selectedIds={selectedIds}
          onToggleRow={handleToggleRow}
          onToggleAll={handleToggleAll}
          onRequestDelete={handleRequestDelete}
          onRemoveMark={async (invoice, markType) => {
            if (!invoice?.id || !markType) return;
            await handleApplyMarks([invoice.id], markType, "remove");
          }}
          onRequestEdit={(invoice) => {
            if (!canManage && isInvoiceEditLocked(invoice)) {
              toast.error(
                "This invoice cannot be edited after Reviewed or Paid.",
              );
              return;
            }
            openEditModal(invoice, { force: canManage });
          }}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
          loading={isLoading}
          pagination={{
            currentPage,
            totalPages,
            onPageChange: setCurrentPage,
            totalItems: totalInvoices,
            itemsPerPage: pageSize,
          }}
        />
      </div>
    </div>
  );
};

export default Invoice;
